import { IProductService, ProductPricingFetchOptions } from '@/domain/services/product.service.interface';
import { ProductEntity } from '@/domain/entities/product.entity';
import { CurrencyAmount } from '@/domain/value-objects/currency-amount.vo';
import { createBffClient } from '@/lib/bff/create-storefront-client';
import type { Product } from '@template-project/storefront-bff-client';

function mapBffProduct(p: Product): ProductEntity {
  return {
    id: p.id,
    uuid: p.id,
    slug: p.slug,
    title: p.title,
    description_html: p.description_html,
    tags: p.tags,
    images: p.images.map((img) => ({
      id: img.id,
      src: img.src,
      alt: img.alt,
      width: img.width,
      height: img.height,
      position: img.position,
      zoomUrl: img.zoomUrl,
    })),
    videos: p.videos?.map((v) => ({
      id: v.id,
      src: v.src,
      alt: v.alt,
      position: v.position,
    })),
    price: p.price as CurrencyAmount,
    compare_at_price: p.compare_at_price as CurrencyAmount | undefined,
    is_out_of_stock: p.is_out_of_stock,
    variants: p.variants.map((v) => ({
      id: v.id,
      uuid: v.id,
      product_id: v.product_id,
      title: v.title,
      price: v.price as CurrencyAmount,
      compare_at_price: v.compare_at_price as CurrencyAmount | undefined,
      sku: v.sku,
      inventory_quantity: v.inventory_quantity,
      enabled: v.enabled,
      variation_attributes: v.variation_attributes,
    })),
    options: p.options,
    status: p.status,
    base_currency: p.base_currency,
    display_currency: p.display_currency,
  };
}

function pricingQuery(options?: ProductPricingFetchOptions) {
  if (!options?.currency) return undefined;
  return { currency: options.currency, country: options.country };
}

export class ProductBffService implements IProductService {
  private client = createBffClient();

  async getProductBySlug(slug: string, options?: ProductPricingFetchOptions): Promise<ProductEntity | null> {
    try {
      const res = await this.client.getProduct(slug, pricingQuery(options));
      return mapBffProduct(res.data);
    } catch {
      return null;
    }
  }

  async getProductById(id: string, options?: ProductPricingFetchOptions): Promise<ProductEntity | null> {
    try {
      const res = await this.client.getProduct(id, pricingQuery(options));
      return mapBffProduct(res.data);
    } catch {
      return null;
    }
  }

  async getRecommendedProducts(
    productId: string,
    limit = 8,
    options?: ProductPricingFetchOptions,
  ): Promise<ProductEntity[]> {
    try {
      const res = await this.client.getRecommendations(productId, {
        limit,
        ...pricingQuery(options),
      });
      return res.data.map(mapBffProduct);
    } catch {
      return [];
    }
  }
}
